import palette from 'src/constants/palette';
import { createTheme } from 'src/lib/style/theme';
import { baseTheme } from 'src/theme';

/**
 * Build vendor-specific theme with light or dark mode support.
 * @param mode - 'light' or 'dark'
 * @returns Theme object with vendor customizations
 */
export const buildVendorTheme = (mode: 'light' | 'dark' = 'light') => {
    const isDark = mode === 'dark';

    const vendorTheme = createTheme({
        ...baseTheme,
        palette: {
            ...baseTheme.palette,
            mode, // Set MUI palette mode
            primary: {
                main: isDark ? '#5F9BFF' : palette.primaryBlue,
                contrastText: palette.textLight,
            },
            secondary: {
                main: palette.primaryTeal,
                dark: palette.tealDark,
                contrastText: palette.textLight,
            },
            text: {
                primary: isDark ? '#E8EAED' : '#0F101A',
                secondary: isDark ? '#9AA0A6' : '#5D5E68',
            },
            background: {
                default: isDark ? '#0F1419' : '#EDF1F7',
                paper: isDark ? '#1A1F26' : '#FFFFFF',
            },
        },
        typography: {
            ...baseTheme.typography,
            body3: {
                ...baseTheme.typography.body3,
                color: isDark ? '#9AA0A6' : '#5D5E68',
            },
        },
        resqPalette: {
            background: {
                default: isDark ? '#0F1419' : '#EDF1F7',
            },
            surface: {
                // Core surfaces
                default: isDark ? '#1A1F26' : '#FFFFFF',
                subdued: isDark ? '#242A33' : '#F0F1F1',
                hovered: isDark ? '#2D3540' : '#F7F7F7',
                pressed: isDark ? '#1E2A3D' : '#EAF1FB',
                selected: isDark ? '#1A3334' : '#DBF6F6',
                // Primary surfaces
                primary: isDark ? '#5F9BFF' : '#2A76D9',
                primaryHovered: isDark ? '#3D82F5' : '#0058CA',
                primaryPressed: isDark ? '#2767D6' : '#004299',
                primaryDisabled: '#81AEE8',
                primarySubduedHovered: isDark ? 'rgba(95, 155, 255, 0.20)' : '#2A76D91A',
                primarySubduedPressed: isDark ? 'rgba(95, 155, 255, 0.36)' : '#2A76D94D',
                // Secondary surfaces
                secondary: isDark ? '#3D3E42' : '#D4D4D7',
                secondaryHovered: isDark ? '#55565A' : '#AAABB0',
                secondaryPressed: isDark ? '#6D6E72' : '#909097',
                secondaryDisabled: isDark ? '#242A33' : '#F0F1F1',
                // Critical surfaces
                critical: isDark ? '#FF4D54' : '#E13139',
                criticalHovered: isDark ? '#E63D44' : '#B3272E',
                criticalPressed: '#881D23',
                criticalDisabled: '#F28F94',
                criticalSubdued: isDark ? '#3D1A1C' : '#FDEDEE',
                criticalSubduedHovered: isDark ? '#5A1E21' : '#E131391A',
                criticalSubduedPressed: isDark ? '#7D2629' : '#E131394D',
                // Success surfaces
                success: '#2A865C',
                successSecondary: '#33A370',
                successHovered: '#216B49',
                successPressed: '#195037',
                successDisabled: '#51BF8C',
                successSubdued: isDark ? '#1A3D2E' : '#E3F5ED',
                successSubduedHovered: isDark ? '#245240' : '#2A865C1A',
                successSubduedPressed: isDark ? '#2E6952' : '#2A865C4D',
                // Highlight surfaces
                highlight: '#7D60E6',
                highlight2: isDark ? '#E0E0E0' : '#0F101A',
                highlightSubdued: isDark ? '#2D2440' : '#F2EFFC',
                // Warning surfaces
                warning: '#F6D06E',
                warningSecondary: '#EB6A1E',
                warningSubdued: isDark ? '#3D3420' : '#FCF0D0',
                // Decorative surfaces (vendor-specific)
                decorative1: '#7D60E6',
                decorative1Subdued: isDark ? '#2D2440' : '#F2EFFC',
                decorative2: '#EB6A1E',
                decorative2Subdued: isDark ? '#3D2518' : '#FDEEE5',
                decorative3: isDark ? '#1A4D4E' : '#00A1A3',
                decorative3Subdued: isDark ? '#1A3334' : '#DBF6F6',
                decorative4: '#AB8D40',
                decorative4Subdued: isDark ? '#3D3420' : '#FCF0D0',
                // Interactive surfaces
                interactive: isDark ? '#1A4D4E' : '#00A1A3',
                interactiveDisabled: isDark ? '#2D5455' : '#8EE3E3',
                tableSubdued: isDark ? '#1A1F26' : '#F7F7F7',
            },
            text: {
                // Core text
                default: isDark ? '#E8EAED' : '#0F101A',
                subdued: isDark ? '#9AA0A6' : '#5D5E68',
                disabled: isDark ? '#5F6368' : '#909097',
                onSecondary: isDark ? '#E8EAED' : '#0F101A',
                // Primary text
                primary: isDark ? '#9DC4FF' : '#0058CA',
                primaryPressed: isDark ? '#6CA0FF' : '#004299',
                primaryDisabled: '#81AEE8',
                onPrimary: '#FFFFFF',
                // Critical text
                critical: isDark ? '#FF5B62' : '#B3272E',
                criticalPressed: '#881D23',
                criticalDisabled: isDark ? '#7D2629' : '#F28F94',
                onCritical: '#FFFFFF',
                onCriticalSubdued: isDark ? '#E8EAED' : '#0F101A',
                // Success text
                success: isDark ? '#51BF8C' : '#216B49',
                successSecondary: isDark ? '#51BF8C' : '#2A865C',
                successPressed: '#195037',
                successDisabled: isDark ? '#2E6952' : '#51BF8C',
                onSuccess: '#FFFFFF',
                onSuccessSubdued: isDark ? '#E8EAED' : '#0F101A',
                // Highlight text
                highlight: isDark ? '#B1A0F0' : '#6248C5',
                onHighlight: '#FFFFFF',
                // Warning text
                warning: isDark ? '#F6D06E' : '#705C29',
                onWarning: isDark ? '#0F101A' : '#0F101A',
                onWarningSubdued: isDark ? '#E8EAED' : '#0F101A',
                // Decorative text (vendor-specific)
                decorative1: isDark ? '#B1A0F0' : '#7D60E6',
                decorative2: '#994613',
                decorative3: isDark ? '#4DD4D6' : '#00696B',
                decorative4: '#705C29',
                onDecorative2Subdued: '#994613',
                // Interactive text
                interactive: isDark ? '#4DD4D6' : '#00696B',
                interactiveHovered: '#004299',
                interactivePressed: '#0F101A',
                interactiveDisabled: isDark ? '#5F6368' : '#909097',
                placeHolder: isDark ? '#5F6368' : '#909097',
                link: isDark ? '#9DC4FF' : '#005BD1',
            },
            icon: {
                // Core icons
                default: isDark ? '#E8EAED' : '#0F101A',
                subdued: isDark ? '#9AA0A6' : '#5D5E68',
                disabled: isDark ? '#5F6368' : '#909097',
                // Primary icons
                primary: '#F75F2F',
                onPrimary: '#FFFFFF',
                tertiary: isDark ? '#9DC4FF' : '#0058CA',
                tertiaryDisabled: '#81AEE8',
                // Warning icons
                warning: isDark ? '#F6D06E' : '#8D7434',
                onWarning: isDark ? '#0F101A' : '#0F101A',
                // Critical icons
                critical: isDark ? '#FF5B62' : '#B3272E',
                criticalDisabled: isDark ? '#7D2629' : '#F28F94',
                onCritical: '#FFFFFF',
                // Success icons
                success: isDark ? '#51BF8C' : '#216B49',
                successDisabled: isDark ? '#2E6952' : '#51BF8C',
                onSuccess: '#FFFFFF',
                // Highlight icons
                highlight: '#7D60E6',
                onHighlight: '#FFFFFF',
                // Decorative icons (vendor-specific)
                decorative1: '#7D60E6',
                decorative1Subdued: isDark ? '#B1A0F0' : '#F4935D',
                decorative2: '#EB6A1E',
                decorative2Subdued: isDark ? '#F4935D' : '#F4935D',
                decorative3: isDark ? '#4DD4D6' : '#00A1A3',
                decorative3Subdued: isDark ? '#4DD4D6' : '#00BEC0',
                decorative4: '#AB8D40',
                decorative4Subdued: '#CAA74B',
                decorative4Bright: '#F6D06E',
                // Interactive icons
                interactive: isDark ? '#9DC4FF' : '#00696B',
                interactiveHovered: isDark ? '#6CA0FF' : '#004299',
                interactivePressed: isDark ? '#2C60CC' : '#0F101A',
                interactiveDisabled: isDark ? '#2D6A6B' : '#909097',
                interactiveCritical: '#E13139',
                interactiveCriticalHovered: '#B3272E',
                interactiveCriticalPressed: '#881D23',
                interactiveCriticalDisabled: '#ED646B',
                focused: '#2A76D9',
            },
            border: {
                // Core borders
                default: isDark ? '#5F6368' : '#AAABB0',
                subdued: isDark ? '#3C4043' : '#D4D4D7',
                hovered: isDark ? '#80868B' : '#909097',
                // Primary borders
                primary: isDark ? '#4C8DFF' : '#2A76D9',
                primaryPressed: isDark ? '#2C60CC' : '#004299',
                primaryDisabled: '#81AEE8',
                onPrimary: isDark ? 'rgba(232, 234, 237, 0.3)' : 'rgba(15, 16, 26, 0.3)',
                // Secondary borders
                secondary: isDark ? '#5F6368' : '#D4D4D7',
                secondaryPressed: isDark ? '#80868B' : '#909097',
                secondaryDisabled: isDark ? '#3C4043' : '#F0F1F1',
                onSecondary: isDark ? 'rgba(232, 234, 237, 0.3)' : 'rgba(15, 16, 26, 0.3)',
                // Critical borders
                critical: isDark ? '#FF4D54' : '#E13139',
                criticalPressed: '#881D23',
                criticalDisabled: '#F28F94',
                criticalSubdued: isDark ? '#7D2629' : '#ED646B',
                onCritical: isDark ? 'rgba(232, 234, 237, 0.3)' : 'rgba(15, 16, 26, 0.3)',
                // Success borders
                success: '#2A865C',
                successPressed: '#195037',
                successDisabled: '#51BF8C',
                successSubdued: '#33A370',
                onSuccess: isDark ? 'rgba(232, 234, 237, 0.3)' : 'rgba(15, 16, 26, 0.3)',
                // Highlight borders
                highlight: '#7D60E6',
                highlightSubdued: '#9780EB',
                // Warning borders
                warning: isDark ? '#F6D06E' : '#8D7434',
                warningSubdued: '#AB8D40',
                // Decorative borders (vendor-specific)
                decorative1: isDark ? '#9780EB' : '#7D60E6',
                decorative2: '#EB6A1E',
                decorative3: isDark ? '#4DD4D6' : '#00A1A3',
                decorative4: '#AB8D40',
                // Interactive borders
                interactive: isDark ? '#4DD4D6' : '#00696B',
                focused: '#2A76D9',
                dark: isDark ? '#80868B' : '#5D5E68',
            },
            divider: {
                default: isDark ? '#3C4043' : '#D4D4D7',
            },
        },
    });

    const existingTabs = vendorTheme.components?.MuiTabs ?? {};
    const existingTabStyles = existingTabs.styleOverrides ?? {};
    const indicatorBase =
        ((existingTabStyles as Record<string, unknown>)?.indicator as Record<string, unknown>) ||
        {};
    const rootBase =
        ((existingTabStyles as Record<string, unknown>)?.root as Record<string, unknown>) || {};

    vendorTheme.components = {
        ...vendorTheme.components,
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 600,
                    padding: vendorTheme.spacing(0, 0, 1),
                    marginRight: vendorTheme.spacing(4),
                    minWidth: 'unset !important',
                    maxWidth: 'unset !important',
                    minHeight: 'unset !important',
                    lineHeight: 'unset !important',
                    color: vendorTheme.resqPalette.text.subdued,
                    '&.Mui-selected': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '&.Mui-focused': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                },
            },
        },
        MuiTabs: {
            ...existingTabs,
            styleOverrides: {
                ...existingTabStyles,
                indicator: {
                    ...indicatorBase,
                    height: '3px',
                    borderRadius: vendorTheme.spacing(1),
                    backgroundColor: vendorTheme.resqPalette.surface.primary,
                },
                root: {
                    ...rootBase,
                    minHeight: 'unset',
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    padding: 0,
                    border: 'none',
                    [vendorTheme.breakpoints.down('lg')]: {
                        paddingLeft: vendorTheme.spacing(4),
                        paddingRight: vendorTheme.spacing(4),
                        borderRadius: 0,
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                    '&.Mui-selected': {
                        backgroundColor: vendorTheme.resqPalette.surface.selected,
                        '&:hover': {
                            backgroundColor: vendorTheme.resqPalette.surface.hovered,
                        },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    '.MuiIconButton-root &': {
                        color: 'inherit',
                    },
                    '.MuiIconButton-root.Mui-disabled &': {
                        color: vendorTheme.resqPalette.icon.disabled,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    '&.Mui-disabled': {
                        color: vendorTheme.resqPalette.icon.disabled,
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.subdued,
                    '& .MuiSvgIcon-root': {
                        color: vendorTheme.resqPalette.icon.subdued,
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.icon.default,
                    minWidth: '40px',
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
                primary: {
                    color: vendorTheme.resqPalette.text.default,
                },
                secondary: {
                    color: vendorTheme.resqPalette.text.subdued,
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    backgroundColor: vendorTheme.resqPalette.surface.tableSubdued,
                    color: vendorTheme.resqPalette.text.default,
                    '&:hover': {
                        backgroundColor: vendorTheme.resqPalette.surface.hovered,
                    },
                },
                body: {
                    color: vendorTheme.resqPalette.text.default,
                    borderTop: `1px solid ${vendorTheme.resqPalette.divider.default}`,
                },
                footer: {
                    color: vendorTheme.resqPalette.text.default,
                    borderTop: `1px solid ${vendorTheme.resqPalette.divider.default}`,
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.subdued,
                    '&:hover': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '&.Mui-active': {
                        color: vendorTheme.resqPalette.text.default,
                        '& .MuiTableSortLabel-icon': {
                            color: vendorTheme.resqPalette.icon.default,
                        },
                    },
                },
                icon: {
                    color: vendorTheme.resqPalette.icon.subdued,
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
                selectLabel: {
                    color: vendorTheme.resqPalette.text.default,
                },
                displayedRows: {
                    color: vendorTheme.resqPalette.text.default,
                },
                select: {
                    color: vendorTheme.resqPalette.text.default,
                },
                selectIcon: {
                    color: vendorTheme.resqPalette.icon.default,
                },
                actions: {
                    color: vendorTheme.resqPalette.icon.default,
                    '& .MuiIconButton-root': {
                        color: `${vendorTheme.resqPalette.icon.default} !important`,
                        '&.Mui-disabled': {
                            color: `${vendorTheme.resqPalette.icon.disabled} !important`,
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '&.MuiTypography-body3': {
                        color: vendorTheme.resqPalette.text.subdued,
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                    '&:before': {
                        backgroundColor: vendorTheme.resqPalette.divider.default,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                    '& .MuiAccordionSummary-expandIconWrapper': {
                        color: vendorTheme.resqPalette.icon.default,
                    },
                },
                expandIconWrapper: {
                    color: vendorTheme.resqPalette.icon.default,
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: vendorTheme.resqPalette.surface.default,
                    color: vendorTheme.resqPalette.text.default,
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    '& a': {
                        color: vendorTheme.resqPalette.text.link,
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: vendorTheme.resqPalette.background.default,
                    color: vendorTheme.resqPalette.text.default,
                    // React Big Calendar overrides for dark mode
                    '& .rbc-today': {
                        backgroundColor: vendorTheme.resqPalette.surface.highlightSubdued,
                    },
                    '& .rbc-selected-cell': {
                        backgroundColor: `${vendorTheme.resqPalette.surface.selected} !important`,
                    },
                    '& .rbc-now': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-current': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-off-range-bg': {
                        backgroundColor: vendorTheme.resqPalette.surface.subdued,
                    },
                    '& .rbc-header': {
                        color: vendorTheme.resqPalette.text.default,
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-date-cell': {
                        color: vendorTheme.resqPalette.text.default,
                    },
                    '& .rbc-month-view': {
                        backgroundColor: vendorTheme.resqPalette.surface.default,
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-day-bg': {
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                    '& .rbc-month-row': {
                        borderColor: vendorTheme.resqPalette.divider.default,
                    },
                },
            },
        },
    };

    return vendorTheme;
};

/**
 * @deprecated Use buildVendorTheme('light') instead. This static export will be removed in Phase 2.
 */
export const vendorTheme = buildVendorTheme('light');
